import React from "react";
import Navbar from "../components/navbar.jsx";
import Footer from "../components/footer.jsx";

export default function About() {
    return (
        <>
            <Navbar />

            <main className="pt-24 pb-16 bg-emerald-50 min-h-screen">
                <div className="max-w-7xl mx-auto px-6">

                    <header className="rounded-2xl bg-gradient-to-r from-emerald-700 to-emerald-600 text-white p-10 md:p-16 shadow-lg">
                        <h1 className="text-3xl md:text-4xl font-extrabold">
                            About Nexus Mental Health Foundation
                        </h1>

                        <p className="mt-4 text-emerald-100 max-w-3xl">
                            Nexus is a multidisciplinary mental health organisation dedicated to restoring wellbeing and
                            enabling meaningful recovery. We combine clinical excellence, compassionate care and measurable outcomes
                            to support people across the lifespan.
                        </p>

                        <div className="mt-6 flex flex-wrap gap-3">
                            <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-medium">
                                Evidence-based care
                            </span>

                            <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-medium">
                                Multidisciplinary teams
                            </span>

                            <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-medium">
                                Patient-centred pathways
                            </span>
                        </div>
                    </header>

                    <div className="mt-10 grid gap-8 lg:grid-cols-[360px_1fr]">

                        {/* Sidebar */}
                        <aside className="sticky top-28 hidden lg:block">
                            <div className="rounded-xl bg-white p-5 shadow ring-1 ring-emerald-100">
                                <h4 className="font-semibold text-emerald-800">
                                    On this page
                                </h4>

                                <nav className="mt-3 flex flex-col gap-2 text-sm">
                                    <a
                                        href="#mission"
                                        className="text-slate-700 hover:text-emerald-700"
                                    >
                                        Mission & Vision
                                    </a>

                                    <a
                                        href="#approach"
                                        className="text-slate-700 hover:text-emerald-700"
                                    >
                                        Our Approach
                                    </a>

                                    <a
                                        href="#team"
                                        className="text-slate-700 hover:text-emerald-700"
                                    >
                                        Clinical Team
                                    </a>

                                    <a
                                        href="#programs"
                                        className="text-slate-700 hover:text-emerald-700"
                                    >
                                        Programs & Services
                                    </a>

                                    <a
                                        href="#development"
                                        className="text-slate-700 hover:text-emerald-700"
                                    >
                                        Development Facility
                                    </a>

                                    <a
                                        href="#community"
                                        className="text-slate-700 hover:text-emerald-700"
                                    >
                                        Community & Research
                                    </a>

                                    <a
                                        href="#contact"
                                        className="text-slate-700 hover:text-emerald-700"
                                    >
                                        Contact & Careers
                                    </a>
                                </nav>
                            </div>
                        </aside>

                        <div className="space-y-8">

                            {/* Mission */}
                            <section
                                id="mission"
                                className="rounded-xl bg-white p-6 shadow ring-1 ring-emerald-50"
                            >
                                <h2 className="text-2xl font-bold text-emerald-800">
                                    Mission & Vision
                                </h2>

                                <p className="mt-4 text-slate-700">
                                    Our mission is to provide safe, accessible and evidence-based mental health care that restores
                                    functioning and dignity. We envision communities where timely, stigma-free care is available to everyone,
                                    and where recovery is supported with clinical skill, empathy and measurable outcomes.
                                </p>

                                <div className="mt-6 grid sm:grid-cols-3 gap-4">
                                    <div className="rounded-lg border p-4">
                                        <h4 className="font-semibold text-emerald-700">
                                            People First
                                        </h4>
                                        <p className="text-sm text-slate-600 mt-2">
                                            Care that centres the person, not just the diagnosis.
                                        </p>
                                    </div>

                                    <div className="rounded-lg border p-4">
                                        <h4 className="font-semibold text-emerald-700">
                                            Evidence Driven
                                        </h4>
                                        <p className="text-sm text-slate-600 mt-2">
                                            Treatments chosen for proven effectiveness and safety.
                                        </p>
                                    </div>

                                    <div className="rounded-lg border p-4">
                                        <h4 className="font-semibold text-emerald-700">
                                            Measured Outcomes
                                        </h4>
                                        <p className="text-sm text-slate-600 mt-2">
                                            Progress tracked with standardised tools & regular reviews.
                                        </p>
                                    </div>
                                </div>
                            </section>

                            {/* Clinical Approach */}
                            <section
                                id="approach"
                                className="rounded-xl bg-white p-6 shadow ring-1 ring-emerald-50"
                            >
                                <h2 className="text-2xl font-bold text-emerald-800">
                                    Our Clinical Approach
                                </h2>

                                <p className="mt-4 text-slate-700">
                                    We integrate psychiatry, psychology, therapy, occupational and speech services into personalised care pathways.
                                    Every plan begins with a thorough assessment and collaborates across specialties to match treatment intensity
                                    with clinical need.
                                </p>

                                <h3 className="mt-4 font-semibold text-slate-800">
                                    Key Elements
                                </h3>

                                <ul className="mt-2 list-disc list-inside text-slate-700 space-y-1">
                                    <li>
                                        Comprehensive assessment and formulation — diagnosis, functional goals and risk review.
                                    </li>
                                    <li>
                                        Evidence-based interventions — medication where indicated, CBT/DBT/ACT, neuromodulation and rehabilitation.
                                    </li>
                                    <li>
                                        Family and community involvement — caregiver training, school liaison and social supports.
                                    </li>
                                    <li>
                                        Outcome monitoring — validated scales and periodic reviews to adapt the plan.
                                    </li>
                                </ul>
                            </section>

                            {/* Clinical Team */}
                            <section
                                id="team"
                                className="rounded-xl bg-white p-6 shadow ring-1 ring-emerald-50"
                            >
                                <h2 className="text-2xl font-bold text-emerald-800">
                                    Clinical Team
                                </h2>

                                <p className="mt-4 text-slate-700">
                                    Our multidisciplinary teams include consultant psychiatrists, clinical psychologists, licensed psychotherapists,
                                    occupational & speech therapists, psychiatric social workers and trained nursing staff. Teams collaborate on shared
                                    care plans and maintain high standards of clinical governance.
                                </p>

                                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                                    <div className="rounded-lg border p-4">
                                        <h4 className="font-semibold">
                                            Consultant Psychiatrists
                                        </h4>
                                        <p className="text-sm text-slate-600 mt-2">
                                            Diagnosis, medication management, complex case oversight and emergency care.
                                        </p>
                                    </div>

                                    <div className="rounded-lg border p-4">
                                        <h4 className="font-semibold">
                                            Therapists & Psychologists
                                        </h4>
                                        <p className="text-sm text-slate-600 mt-2">
                                            Structured psychotherapy, assessments and targeted interventions across ages.
                                        </p>
                                    </div>
                                </div>
                            </section>

                            {/* Programs */}
                            <section
                                id="programs"
                                className="rounded-xl bg-white p-6 shadow ring-1 ring-emerald-50"
                            >
                                <h2 className="text-2xl font-bold text-emerald-800">
                                    Programs & Services
                                </h2>

                                <p className="mt-4 text-slate-700">
                                    Nexus provides a wide range of services from outpatient therapy to advanced neuromodulation and inpatient care.
                                    Core programs include:
                                </p>

                                <ul className="mt-4 list-disc list-inside text-slate-700 space-y-2">
                                    <li>
                                        <strong>Outpatient Psychiatry & Psychotherapy:</strong>{" "}
                                        Individual, couple and group therapies across evidence-based modalities.
                                    </li>

                                    <li>
                                        <strong>Child & Adolescent Pathways:</strong>{" "}
                                        Developmental assessments, play therapy, school liaison and parent coaching.
                                    </li>

                                    <li>
                                        <strong>Occupational & Speech Therapy:</strong>{" "}
                                        Functional rehabilitation and communication support for children and adults.
                                    </li>

                                    <li>
                                        <strong>Advanced Treatments:</strong>{" "}
                                        Deep TMS, ECT and neuromodulation for selected treatment-resistant cases, under strict clinical governance.
                                    </li>

                                    <li>
                                        <strong>Psychometric & Neurocognitive Testing:</strong>{" "}
                                        Objective assessments to guide diagnosis, education and treatment planning.
                                    </li>

                                    <li>
                                        <strong>Hospital Services:</strong>{" "}
                                        Acute and sub-acute inpatient care where 24/7 medical supervision is needed.
                                    </li>
                                </ul>

                                <div className="mt-6">
                                    <a
                                        href="/services"
                                        className="inline-flex items-center rounded-full bg-emerald-700 px-5 py-3 text-white font-semibold shadow"
                                    >
                                        Explore Services
                                    </a>
                                </div>
                            </section>

                            {/* Development Facility */}
                            <section
                                id="development"
                                className="rounded-xl bg-white p-6 shadow ring-1 ring-emerald-50"
                            >
                                <h2 className="text-2xl font-bold text-emerald-800">
                                    Development Facility at Nexus
                                </h2>

                                <p className="mt-2 text-xl font-semibold text-emerald-700">
                                    Nurturing Potential, At Every Stage of Life
                                </p>

                                <p className="mt-4 text-slate-700">
                                    At Nexus Mental Health Foundation, our Development Facility is dedicated to holistic growth,
                                    independence, and inclusion. We believe development is a lifelong journey. Our evidence-based
                                    programs are designed separately for children and adults, with individualized care plans,
                                    trained professionals, and family involvement at the core.
                                </p>

                                {/* Child Development Facility */}
                                <div className="mt-8 rounded-xl bg-emerald-50 p-6">
                                    <h3 className="text-xl font-bold text-emerald-800">
                                        1. Child Development Facility
                                    </h3>

                                    <p className="mt-2 font-semibold text-emerald-700">
                                        Early Years, Strong Foundations
                                    </p>

                                    <p className="mt-3 text-slate-700">
                                        Our Child Development Centre focuses on early identification, intervention,
                                        and consistent support for children with developmental needs.
                                    </p>

                                    <h4 className="mt-5 font-semibold text-slate-800">
                                        Our Services Include:
                                    </h4>

                                    <ul className="mt-3 list-disc list-inside text-slate-700 space-y-2">
                                        <li>
                                            <strong>Early Intervention Program (EIP):</strong>{" "}
                                            For children from 0-6 years with developmental delays, Autism, ADHD,
                                            Down Syndrome and Global Developmental Delay.
                                        </li>

                                        <li>
                                            <strong>Occupational Therapy & Sensory Integration:</strong>{" "}
                                            To improve fine motor skills, gross motor skills, sensory processing,
                                            daily living activities and handwriting.
                                        </li>

                                        <li>
                                            <strong>Speech & Language Therapy:</strong>{" "}
                                            For delayed speech, articulation, fluency, social communication and feeding issues.
                                        </li>

                                        <li>
                                            <strong>Special Education & Remedial Education:</strong>{" "}
                                            Individualized Education Plans (IEP) for learning disabilities,
                                            intellectual disabilities and academic difficulties.
                                        </li>

                                        <li>
                                            <strong>Applied Behaviour Analysis (ABA) & Behaviour Modification:</strong>{" "}
                                            Positive behaviour support for challenging behaviours, attention and compliance.
                                        </li>

                                        <li>
                                            <strong>Play Therapy & Child Counselling:</strong>{" "}
                                            To build emotional regulation, social interaction, confidence and peer relationships.
                                        </li>

                                        <li>
                                            <strong>Parent Training & Family Counselling:</strong>{" "}
                                            We empower parents to continue development at home with regular training and progress reports.
                                        </li>
                                    </ul>

                                    <h4 className="mt-6 font-semibold text-slate-800">
                                        We Support Children With:
                                    </h4>

                                    <p className="mt-2 text-slate-700">
                                        Autism Spectrum Disorder (ASD), ADHD, Speech Delay, Intellectual Disability,
                                        Learning Disabilities (Dyslexia, Dysgraphia, Dyscalculia), Cerebral Palsy,
                                        Developmental Delays.
                                    </p>
                                </div>

                                {/* Adult Development Facility */}
                                <div className="mt-8 rounded-xl bg-slate-50 p-6">
                                    <h3 className="text-xl font-bold text-emerald-800">
                                        2. Adult Development Facility
                                    </h3>

                                    <p className="mt-2 font-semibold text-emerald-700">
                                        Building Independence, Dignity & Skills
                                    </p>

                                    <p className="mt-3 text-slate-700">
                                        Our Adult Development Centre focuses on recovery, rehabilitation and
                                        reintegration into community and work life.
                                    </p>

                                    <h4 className="mt-5 font-semibold text-slate-800">
                                        Our Services Include:
                                    </h4>

                                    <ul className="mt-3 list-disc list-inside text-slate-700 space-y-2">
                                        <li>
                                            <strong>Psychosocial Rehabilitation (PSR):</strong>{" "}
                                            Structured program to improve daily living, self-care, money management
                                            and community living skills.
                                        </li>

                                        <li>
                                            <strong>Vocational Training & Skill Development:</strong>{" "}
                                            Job-oriented training like computer basics, office skills, tailoring,
                                            art & craft, agriculture and other work skills based on interest.
                                        </li>

                                        <li>
                                            <strong>Independent Living Skills:</strong>{" "}
                                            Training for cooking, hygiene, travel training, time management and home management.
                                        </li>

                                        <li>
                                            <strong>Adult Counselling & Psychotherapy:</strong>{" "}
                                            Individual, group and family counselling for anxiety, depression, stress,
                                            OCD, bipolar and other mental health concerns.
                                        </li>

                                        <li>
                                            <strong>Social Skills & Life Skills Development:</strong>{" "}
                                            Building communication, interpersonal relationships, emotional regulation and problem-solving.
                                        </li>

                                        <li>
                                            <strong>Cognitive Remediation & Brain Wellness:</strong>{" "}
                                            Activities to improve memory, attention, concentration and decision-making.
                                        </li>
                                    </ul>
                                </div>
                            </section>

                            {/* Community */}
                            <section
                                id="community"
                                className="rounded-xl bg-white p-6 shadow ring-1 ring-emerald-50"
                            >
                                <h2 className="text-2xl font-bold text-emerald-800">
                                    Community, Research & Training
                                </h2>

                                <p className="mt-4 text-slate-700">
                                    We engage in community outreach, public education and professional training to build capacity and reduce stigma.
                                    Nexus contributes to translational research and audits outcomes so practice evolves in line with the best available evidence.
                                </p>

                                <h4 className="mt-4 font-semibold text-slate-800">
                                    What we do
                                </h4>

                                <ul className="mt-2 list-disc list-inside text-slate-700 space-y-1">
                                    <li>
                                        Public mental health awareness and school workshops.
                                    </li>
                                    <li>
                                        Clinical audits, outcome measurement and service evaluation.
                                    </li>
                                    <li>
                                        Training programs for clinicians, caregivers and allied professionals.
                                    </li>
                                </ul>
                            </section>

                            {/* Contact */}
                            <section
                                id="contact"
                                className="rounded-xl bg-white p-6 shadow ring-1 ring-emerald-50"
                            >
                                <h2 className="text-2xl font-bold text-emerald-800">
                                    Contact, Careers & Support
                                </h2>

                                <p className="mt-4 text-slate-700">
                                    To book an appointment, enquire about programs or explore careers and volunteering,
                                    please use our contact page or call your nearest Nexus clinic.
                                </p>

                                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                    <div className="rounded-lg border p-4">
                                        <h4 className="font-semibold">
                                            Careers & Training
                                        </h4>

                                        <p className="text-sm text-slate-600 mt-2">
                                            We hire clinicians, therapists and allied professionals. Check openings
                                            or email us to discuss placements and internships.
                                        </p>
                                    </div>

                                    <div className="rounded-lg border p-4">
                                        <h4 className="font-semibold">
                                            Support Our Work
                                        </h4>

                                        <p className="text-sm text-slate-600 mt-2">
                                            If you wish to support community programs or research, contact our
                                            partnerships team for collaboration opportunities.
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <a
                                        href="/contact"
                                        className="inline-flex items-center rounded-full bg-emerald-700 px-5 py-3 text-white font-semibold shadow"
                                    >
                                        Contact Us
                                    </a>
                                </div>
                            </section>

                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </>
    );
}